/**
 * TocPopup — Table of Contents / Section Navigator popup.
 *
 * Triggered by clicking the scope chip in CommandRail.
 * Opens as a centered fixed modal; does not shift or resize the existing layout.
 *
 * Features:
 *   - Search/filter (number + title, case-insensitive)
 *   - Collapsible nested hierarchy (browse mode)
 *   - Active section highlight (inspector > cursor priority)
 *   - Keyboard navigation (↑↓ to move, Enter to navigate, Escape to close)
 *   - Smooth entry animation; prefers-reduced-motion respected
 *
 * SRP: pure presentation + local interaction state.
 * Navigation is delegated to the `onNavigate` prop (shell calls navigateToSection).
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTocData } from "../hooks/useTocData";
import {
	type TocItem,
	buildInitialCollapsed,
	searchToc,
	visibleTocItems,
} from "../toc";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
	backdrop: {
		position: "fixed",
		inset: 0,
		background: "rgba(15, 10, 30, 0.45)",
		backdropFilter: "blur(3px)",
		WebkitBackdropFilter: "blur(3px)",
		zIndex: 500,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: "16px",
		fontFamily: fonts.body,
	},
	panel: {
		// position:relative overrides the browser UA position:absolute on <dialog>,
		// which would otherwise take the element out of the flex container's flow
		// and break the backdrop's justifyContent:center alignment.
		position: "relative",
		background: surfaceColors.paper,
		border: `1px solid ${surfaceColors.lineStrong}`,
		borderTop: `3px solid ${brandColors.primary}`,
		borderRadius: "4px",
		boxShadow: "0 24px 56px rgba(15, 10, 30, 0.30)",
		maxWidth: "340px",
		width: "100%",
		maxHeight: "80vh",
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		margin: 0,
		padding: 0,
		animationName: {
			from: { opacity: 0, transform: "scale(0.96) translateY(-6px)" },
			to: { opacity: 1, transform: "scale(1) translateY(0)" },
		},
		animationDuration: "160ms",
		animationTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
		animationFillMode: "both",
		"@media (prefers-reduced-motion: reduce)": {
			animationDuration: "0ms",
		},
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "12px 14px 10px",
		borderBottom: `1px solid ${surfaceColors.line}`,
		flexShrink: 0,
	},
	headerTitle: {
		fontFamily: fonts.display,
		fontSize: "13px",
		fontWeight: 700,
		color: inkColors.ink,
		letterSpacing: "0.2px",
	},
	closeBtn: {
		background: "none",
		border: "none",
		padding: "2px 5px",
		cursor: "pointer",
		color: inkColors.ink4,
		fontSize: "16px",
		lineHeight: 1,
		borderRadius: "2px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		"&:hover": {
			color: inkColors.ink2,
			background: surfaceColors.paperDim,
		},
		"&:focus-visible": {
			outline: `2px solid ${brandColors.primary}`,
			outlineOffset: "1px",
		},
	},
	searchRow: {
		padding: "8px 10px",
		borderBottom: `1px solid ${surfaceColors.line}`,
		flexShrink: 0,
	},
	searchInput: {
		width: "100%",
		border: `1px solid ${surfaceColors.lineStrong}`,
		borderRadius: "3px",
		padding: "5px 9px",
		fontSize: "12px",
		fontFamily: fonts.body,
		color: inkColors.ink2,
		background: surfaceColors.paperDim,
		outline: "none",
		boxSizing: "border-box",
		"&:focus": {
			border: `1px solid ${brandColors.primary}`,
			background: surfaceColors.paper,
		},
		"&::placeholder": {
			color: inkColors.ink5,
		},
	},
	scrollContainer: {
		overflowY: "auto",
		flex: 1,
		padding: "4px 0",
	},
	emptyState: {
		padding: "20px 14px",
		fontSize: "12px",
		color: inkColors.ink4,
		textAlign: "center",
		fontFamily: fonts.body,
		fontStyle: "italic",
	},
	row: {
		display: "flex",
		alignItems: "center",
		gap: "6px",
		// 4px left padding reserved for the active accent bar; right stays at 12px
		padding: "5px 12px 5px 4px",
		cursor: "pointer",
		background: "none",
		border: "none",
		borderLeft: "2px solid transparent",
		width: "100%",
		textAlign: "left",
		borderRadius: 0,
		"&:hover": {
			background: surfaceColors.paperDim,
		},
		// Suppress browser focus ring — keyboard nav uses arrow keys, not tab
		"&:focus-visible": {
			outline: "none",
			background: surfaceColors.paperDim,
		},
	},
	rowActive: {
		borderLeft: `2px solid ${brandColors.primary}`,
		background: "none",
		"&:hover": {
			background: surfaceColors.paperDim,
		},
	},
	collapseToggle: {
		background: "none",
		border: "none",
		padding: 0,
		cursor: "pointer",
		color: inkColors.ink5,
		fontSize: "9px",
		lineHeight: 1,
		width: "14px",
		height: "14px",
		flexShrink: 0,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		borderRadius: "2px",
		"&:hover": {
			color: inkColors.ink3,
		},
		"&:focus-visible": {
			outline: `1px solid ${brandColors.primary}`,
		},
	},
	collapseTogglePlaceholder: {
		width: "14px",
		flexShrink: 0,
		display: "inline-block",
	},
	sectionNumber: {
		fontFamily: fonts.mono,
		fontSize: "10px",
		fontWeight: 700,
		color: inkColors.ink4,
		flexShrink: 0,
		minWidth: "36px",
		letterSpacing: "0.2px",
	},
	sectionNumberActive: {
		color: brandColors.primary,
	},
	sectionTitle: {
		fontFamily: fonts.body,
		fontSize: "12px",
		color: inkColors.ink2,
		flex: 1,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	sectionTitleActive: {
		color: inkColors.ink,
		fontWeight: 600,
	},
});

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface TocPopupProps {
	/** Controls visibility. When false, returns null — no DOM cost. */
	open: boolean;
	onClose: () => void;
	/** From `docConfig.skeleton`. Null when no doc type is selected. */
	skeleton: [string, string, number][] | null;
	/**
	 * Currently active section number to highlight.
	 * Pass `null` when scope kind is "document" (nothing focused).
	 */
	activeSectionNumber: string | null;
	/**
	 * Called when the writer picks a section.
	 * Shell calls `navigateToSection(sectionNumber)` and closes the popup.
	 */
	onNavigate: (sectionNumber: string) => void;
	/**
	 * Called with a section number while the writer's mouse is over that
	 * row, and with `null` when the mouse leaves it. Lets the shell preview
	 * the hovered section as the chat rail's scope ahead of a click. Optional —
	 * omitted callers get the pre-hover-preview behaviour unchanged.
	 */
	onHoverSection?: (sectionNumber: string | null) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TocPopup({
	open,
	onClose,
	skeleton,
	activeSectionNumber,
	onNavigate,
	onHoverSection,
}: TocPopupProps) {
	const styles = useStyles();
	const { tree, flat } = useTocData(skeleton);

	const [query, setQuery] = useState("");
	const [collapsed, setCollapsed] = useState<Set<string>>(() =>
		buildInitialCollapsed(tree),
	);
	const [focusedIndex, setFocusedIndex] = useState(0);

	const searchInputRef = useRef<HTMLInputElement>(null);

	// Reset collapse state when tree changes (new skeleton loaded)
	const prevTreeRef = useRef(tree);
	useEffect(() => {
		if (prevTreeRef.current !== tree) {
			prevTreeRef.current = tree;
			setCollapsed(buildInitialCollapsed(tree));
		}
	}, [tree]);

	// Focus the search input when the popup opens (avoids autoFocus a11y rule)
	useEffect(() => {
		if (open) {
			setQuery("");
			setFocusedIndex(0);
			// Defer to next tick so the dialog is fully mounted before focusing
			const id = setTimeout(() => searchInputRef.current?.focus(), 0);
			return () => clearTimeout(id);
		}
	}, [open]);

	const isSearchMode = query.trim().length > 0;

	const searchResults = useMemo(() => searchToc(flat, query), [flat, query]);

	const browseItems = useMemo(
		() => visibleTocItems(tree, collapsed),
		[tree, collapsed],
	);

	const visibleItems = isSearchMode ? searchResults : browseItems;

	const handleToggleCollapse = useCallback((sectionNumber: string) => {
		setCollapsed((prev) => {
			const next = new Set(prev);
			if (next.has(sectionNumber)) next.delete(sectionNumber);
			else next.add(sectionNumber);
			return next;
		});
	}, []);

	const handleNavigate = useCallback(
		(sectionNumber: string) => {
			onNavigate(sectionNumber);
		},
		[onNavigate],
	);

	// Keyboard handler — mounted only while open
	useEffect(() => {
		if (!open) return;
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
				return;
			}
			if (e.key === "ArrowDown") {
				e.preventDefault();
				setFocusedIndex((i) => Math.min(i + 1, visibleItems.length - 1));
				return;
			}
			if (e.key === "ArrowUp") {
				e.preventDefault();
				setFocusedIndex((i) => Math.max(i - 1, 0));
				return;
			}
			if (e.key === "Enter") {
				const item = visibleItems[focusedIndex];
				if (item) handleNavigate(item.sectionNumber);
			}
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [open, focusedIndex, visibleItems, handleNavigate, onClose]);

	// Clamp focusedIndex when visible list shrinks (e.g. narrowing search)
	useEffect(() => {
		if (visibleItems.length > 0) {
			setFocusedIndex((i) => Math.min(i, visibleItems.length - 1));
		}
	}, [visibleItems.length]);

	if (!open) return null;

	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: Escape is handled by the window keydown listener mounted in useEffect
		<div
			className={styles.backdrop}
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
			role="presentation"
		>
			<dialog
				open
				className={styles.panel}
				aria-label="Document section navigator"
			>
				{/* Header */}
				<div className={styles.header}>
					<span className={styles.headerTitle}>Table of Contents</span>
					<button
						type="button"
						className={styles.closeBtn}
						onClick={onClose}
						aria-label="Close section navigator"
					>
						×
					</button>
				</div>

				{/* Search */}
				<div className={styles.searchRow}>
					<input
						ref={searchInputRef}
						type="search"
						className={styles.searchInput}
						placeholder="Filter by number or title…"
						value={query}
						onChange={(e) => {
							setQuery(e.target.value);
							setFocusedIndex(0);
						}}
						aria-label="Filter sections"
						autoComplete="off"
						spellCheck={false}
					/>
				</div>

				{/* Section list */}
				<div className={styles.scrollContainer}>
					{visibleItems.length === 0 ? (
						<div className={styles.emptyState}>
							{flat.length === 0
								? "No sections defined for this document type."
								: "No sections match your filter."}
						</div>
					) : isSearchMode ? (
						<SearchList
							items={searchResults}
							activeSectionNumber={activeSectionNumber}
							onNavigate={handleNavigate}
							onHoverSection={onHoverSection}
							styles={styles}
						/>
					) : (
						<BrowseTree
							tree={tree}
							collapsed={collapsed}
							activeSectionNumber={activeSectionNumber}
							onNavigate={handleNavigate}
							onToggleCollapse={handleToggleCollapse}
							onHoverSection={onHoverSection}
							styles={styles}
						/>
					)}
				</div>
			</dialog>
		</div>
	);
}

// ---------------------------------------------------------------------------
// SearchList — flat filtered list for search mode
// ---------------------------------------------------------------------------

interface SearchListProps {
	items: TocItem[];
	activeSectionNumber: string | null;
	onNavigate: (sectionNumber: string) => void;
	onHoverSection?: (sectionNumber: string | null) => void;
	styles: ReturnType<typeof useStyles>;
}

function SearchList({
	items,
	activeSectionNumber,
	onNavigate,
	onHoverSection,
	styles,
}: SearchListProps) {
	return (
		<>
			{items.map((item) => {
				const isActive = item.sectionNumber === activeSectionNumber;
				return (
					<button
						key={item.sectionNumber}
						type="button"
						aria-current={isActive ? "true" : undefined}
						className={mergeClasses(styles.row, isActive && styles.rowActive)}
						onClick={() => onNavigate(item.sectionNumber)}
						onMouseEnter={() => onHoverSection?.(item.sectionNumber)}
						onMouseLeave={() => onHoverSection?.(null)}
						title={`${item.sectionNumber} ${item.title}`}
					>
						<span
							className={mergeClasses(
								styles.sectionNumber,
								isActive && styles.sectionNumberActive,
							)}
						>
							{item.sectionNumber}
						</span>
						<span
							className={mergeClasses(
								styles.sectionTitle,
								isActive && styles.sectionTitleActive,
							)}
						>
							{item.title}
						</span>
					</button>
				);
			})}
		</>
	);
}

// ---------------------------------------------------------------------------
// BrowseTree — collapsible hierarchy for browse mode
// ---------------------------------------------------------------------------

interface BrowseTreeProps {
	tree: TocItem[];
	collapsed: Set<string>;
	activeSectionNumber: string | null;
	onNavigate: (sectionNumber: string) => void;
	onToggleCollapse: (sectionNumber: string) => void;
	onHoverSection?: (sectionNumber: string | null) => void;
	styles: ReturnType<typeof useStyles>;
}

function BrowseTree({
	tree,
	collapsed,
	activeSectionNumber,
	onNavigate,
	onToggleCollapse,
	onHoverSection,
	styles,
}: BrowseTreeProps) {
	return (
		<>
			{tree.map((root) => (
				<BrowseSubtree
					key={root.sectionNumber}
					item={root}
					collapsed={collapsed}
					activeSectionNumber={activeSectionNumber}
					onNavigate={onNavigate}
					onToggleCollapse={onToggleCollapse}
					onHoverSection={onHoverSection}
					styles={styles}
				/>
			))}
		</>
	);
}

interface BrowseSubtreeProps {
	item: TocItem;
	collapsed: Set<string>;
	activeSectionNumber: string | null;
	onNavigate: (sectionNumber: string) => void;
	onToggleCollapse: (sectionNumber: string) => void;
	onHoverSection?: (sectionNumber: string | null) => void;
	styles: ReturnType<typeof useStyles>;
}

function BrowseSubtree({
	item,
	collapsed,
	activeSectionNumber,
	onNavigate,
	onToggleCollapse,
	onHoverSection,
	styles,
}: BrowseSubtreeProps) {
	const isActive = item.sectionNumber === activeSectionNumber;
	const isCollapsed = collapsed.has(item.sectionNumber);
	const hasChildren = item.children.length > 0;
	// Indent each depth level — top-level items use full row padding
	const toggleIndent = `${12 + Math.max(0, item.depth - 1) * 12}px`;

	return (
		<>
			<div style={{ display: "flex", alignItems: "center" }}>
				{hasChildren ? (
					<button
						type="button"
						className={styles.collapseToggle}
						style={{ marginLeft: toggleIndent }}
						onClick={(e) => {
							e.stopPropagation();
							onToggleCollapse(item.sectionNumber);
						}}
						aria-label={
							isCollapsed
								? `Expand ${item.sectionNumber}`
								: `Collapse ${item.sectionNumber}`
						}
						aria-expanded={!isCollapsed}
					>
						{isCollapsed ? "▶" : "▼"}
					</button>
				) : (
					<span
						className={styles.collapseTogglePlaceholder}
						style={{ marginLeft: toggleIndent }}
					/>
				)}
				<button
					type="button"
					aria-current={isActive ? "true" : undefined}
					className={mergeClasses(styles.row, isActive && styles.rowActive)}
					style={{ paddingLeft: item.depth === 0 ? "0px" : "6px", flex: 1 }}
					onClick={() => onNavigate(item.sectionNumber)}
					onMouseEnter={() => onHoverSection?.(item.sectionNumber)}
					onMouseLeave={() => onHoverSection?.(null)}
					title={`${item.sectionNumber} ${item.title}`}
				>
					<span
						className={mergeClasses(
							styles.sectionNumber,
							isActive && styles.sectionNumberActive,
						)}
					>
						{item.sectionNumber}
					</span>
					<span
						className={mergeClasses(
							styles.sectionTitle,
							isActive && styles.sectionTitleActive,
						)}
					>
						{item.title}
					</span>
				</button>
			</div>

			{!isCollapsed &&
				item.children.map((child) => (
					<BrowseSubtree
						key={child.sectionNumber}
						item={child}
						collapsed={collapsed}
						activeSectionNumber={activeSectionNumber}
						onNavigate={onNavigate}
						onToggleCollapse={onToggleCollapse}
						onHoverSection={onHoverSection}
						styles={styles}
					/>
				))}
		</>
	);
}
